<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="cogs"/> {{ $t('systemEmailHistory') }}</h1>
        <p>{{ $t('systemEmailHistoryDesc') }}</p>
      </div>
    </div>

    <div class="row">
      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <client-select :model="filterFormModel" class="mr-1 mb-1" attribute="client" @change="search"
                           :isFilter="true" :place-holder="$t('selectClient')"></client-select>
            <form-select :model="filterFormModel" attribute="category" class="mr-1 mb-1" :options="categoryOptions"></form-select>
            <form-select :model="filterFormModel" attribute="status" class="mr-1 mb-1" :options="[
              {text: $t('allStatuses'), value: null},
              {text: $t('success'), value: 'success'},
              {text: $t('failed'), value: 'failed'},
            ]"></form-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" class="mr-1 mb-1" :defaultShortcut="3"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card" :class="{ 'log-card--error': item.sendError }"
               :style="{ borderLeftColor: item.sendError ? '#dc3545' : '#6c757d' }"
               @click="showEmail(item)" role="button">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm') }}</div>
              <div class="log-meta" v-if="item.sendTime">Gửi: {{ item.sendTime | moment('HH:mm') }}</div>
            </div>

            <div class="log-card__type">
              <span class="log-type-pill" :style="{ background: categoryColor(item.category) }">
                {{ categoryLabel(item.category) }}
              </span>
            </div>

            <div class="log-card__main">
              <div class="log-row-1"><strong>{{ item.subject || '(không tiêu đề)' }}</strong></div>
              <div class="log-row-2">
                <span class="log-meta mr-1">Tới:</span>
                <span>{{ item.toEmail || '—' }}</span>
              </div>
              <div class="log-row-3 text-danger" v-if="item.sendErrorMsg">
                <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>
                {{ item.sendErrorMsg }}
              </div>
              <div class="log-row-3" v-if="item.client">
                <font-awesome-icon icon="building" class="text-muted mr-1"/>
                <span class="log-meta">{{ item.client.domain }}</span>
              </div>
            </div>

            <div class="log-card__status">
              <b-badge v-if="item.sendError" variant="danger">{{ $t('failed') }}</b-badge>
              <b-badge v-else variant="success">{{ $t('success') }}</b-badge>
            </div>

            <div class="log-card__chev"><font-awesome-icon icon="chevron-right"/></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-5" v-if="!items.length">
        <font-awesome-icon icon="inbox" size="3x" class="mb-3 d-block mx-auto"/>
        <div>{{ $t('notFoundSearch') }}</div>
      </div>

      <div class="col-12" v-if="itemCount > filterFormModel.pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal size="xl" v-model="showForm" hide-footer scrollable :modal-class="['log-modal']">
      <template #modal-title>
        <span v-if="email && email._id">
          <span class="log-type-pill" :style="{ background: categoryColor(email.category) }">
            {{ categoryLabel(email.category) }}
          </span>
          <span class="ml-2">{{ $t('emailDetail') }}</span>
        </span>
      </template>

      <div v-if="email && email._id">
        <div class="log-meta-grid">
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('status') }}</div>
            <div>
              <b-badge v-if="email.sendError" variant="danger">{{ $t('failed') }}</b-badge>
              <b-badge v-else variant="success">{{ $t('success') }}</b-badge>
            </div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('createTime') }}</div>
            <div>{{ email.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</div>
          </div>
          <div class="log-meta-cell" v-if="email.sendTime">
            <div class="log-meta-label">{{ $t('sendTime') }}</div>
            <div>{{ email.sendTime | moment('DD/MM/YYYY HH:mm:ss') }}</div>
          </div>
          <div class="log-meta-cell" v-if="email.client">
            <div class="log-meta-label">{{ $t('selectClient') }}</div>
            <div>{{ email.client.domain }}</div>
          </div>
        </div>

        <div class="log-meta-grid mt-2">
          <div class="log-meta-cell log-meta-cell--wide">
            <div class="log-meta-label">{{ $t('fromEmail') }}</div>
            <div>{{ formatFrom(email) }}</div>
          </div>
          <div class="log-meta-cell log-meta-cell--wide">
            <div class="log-meta-label">{{ $t('toEmail') }}</div>
            <div>{{ email.toEmail || '—' }}</div>
          </div>
        </div>

        <div class="log-meta-grid mt-2" v-if="email.emailId">
          <div class="log-meta-cell" style="grid-column: span 4">
            <div class="log-meta-label">Postmark MessageID</div>
            <div>{{ email.emailId }}</div>
          </div>
        </div>

        <div class="alert alert-danger mt-3" v-if="email.sendErrorMsg">
          <strong>{{ $t('error') }}:</strong> {{ email.sendErrorMsg }}
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">{{ $t('emailSubject') }}</div>
          <div class="log-content-block"><strong>{{ email.subject }}</strong></div>
        </div>

        <div class="log-section" v-if="email.content">
          <div class="log-section__label log-section__label--big">{{ $t('content') }}</div>
          <!-- iframe: hiển thị đúng giao diện email người nhận; tự co cao theo nội dung (không cuộn trong khung nhỏ).
               sandbox="allow-same-origin" (KHÔNG allow-scripts) → script trong email vẫn bị chặn, chỉ cho phép đọc DOM để đo chiều cao -->
          <iframe ref="emailFrame" :srcdoc="email.content" class="email-preview-frame"
                  sandbox="allow-same-origin" @load="resizeEmailFrame"></iframe>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
import list from '../../mixins/List';
import utils from '../../utils';
import ClientSelect from "@/components/ClientSelect";
import TimeRangePicker from "../../components/TimeRangePicker";
import _ from 'lodash';

export default {
  components: { TimeRangePicker, ClientSelect },
  mixins: [list, utils],
  data() {
    return {
      userRole: this.$store.state.user.role,
      service: '/system-email',
      autoLoad: true,
      showForm: false,
      email: {},
      defaultFilterFormModel: {
        client: null,
        category: null,
        status: null
      }
    }
  },
  computed: {
    categoryOptions() {
      const cats = ['passwordReset', 'register', 'userInvite', 'emailSenderVerify',
        'profileEmailChange', 'phoneNumberAlert', 'faultDetection', 'kyc',
        'invoiceChange', 'automation', 'survey', 'other'];
      return [{ text: this.$t('allCategories'), value: null }]
        .concat(cats.map(c => ({ text: this.$t('cat_' + c), value: c })));
    }
  },
  methods: {
    formatFrom(email) {
      if (!email) return '—';
      if (email.fromName) {
        return email.fromName + ' <' + (email.fromEmail || '') + '>';
      }
      return email.fromEmail || '—';
    },
    search: _.debounce(function () {
      this.filterFormModel.page = 1;
      this.load();
    }, 200),
    showEmail(email) {
      this.showForm = true;
      this.email = email;
    },
    resizeEmailFrame() {
      // Co chiều cao iframe vừa đúng nội dung email → xem trọn không cuộn trong khung nhỏ
      const f = this.$refs.emailFrame;
      if (!f) return;
      try {
        const doc = f.contentDocument || (f.contentWindow && f.contentWindow.document);
        const h = doc && doc.body ? doc.body.scrollHeight : 0;
        if (h) f.style.height = (h + 24) + 'px';
      } catch (e) {
        // cross-origin/sandbox chặn đọc → giữ chiều cao mặc định
      }
    },
    categoryLabel(category) {
      return category ? this.$t('cat_' + category) : this.$t('cat_unknown');
    },
    categoryColor(category) {
      const map = {
        passwordReset: '#e67e22', register: '#27ae60', userInvite: '#2980b9',
        emailSenderVerify: '#8e44ad', profileEmailChange: '#16a085',
        phoneNumberAlert: '#c0392b', faultDetection: '#d35400',
        kyc: '#2c3e50', invoiceChange: '#7f8c8d', automation: '#34495e',
        survey: '#9b59b6', other: '#95a5a6'
      };
      return map[category] || '#95a5a6';
    }
  },
  i18n: {
    messages: {
      en: {
        systemEmailHistory: 'System email log',
        systemEmailHistoryDesc: 'Log of system emails sent (password reset, verification, KYC, invoice...).',
        content: 'Content',
        fromEmail: 'From',
        toEmail: 'To',
        createTime: 'Created',
        sendTime: 'Sent',
        status: 'Status',
        emailSubject: 'Subject',
        emailDetail: 'Email detail',
        allStatuses: 'All statuses',
        failed: 'Failed',
        allCategories: 'All categories',
        cat_unknown: 'Unknown',
        cat_passwordReset: 'Password reset',
        cat_register: 'Registration',
        cat_userInvite: 'User invite',
        cat_emailSenderVerify: 'Sender verify',
        cat_profileEmailChange: 'Profile email change',
        cat_phoneNumberAlert: 'Phone number alert',
        cat_faultDetection: 'Fault detection',
        cat_kyc: 'KYC',
        cat_invoiceChange: 'Invoice change',
        cat_automation: 'Automation',
        cat_survey: 'Survey',
        cat_other: 'Other',
      },
      vi: {
        systemEmailHistory: 'Nhật ký email hệ thống',
        systemEmailHistoryDesc: 'Nhật ký email hệ thống đã gửi (đặt lại mật khẩu, xác thực, KYC, hoá đơn...).',
        content: 'Nội dung',
        fromEmail: 'Từ',
        toEmail: 'Tới',
        createTime: 'Tạo lúc',
        sendTime: 'Gửi lúc',
        status: 'Trạng thái',
        emailSubject: 'Tiêu đề',
        emailDetail: 'Chi tiết email',
        allStatuses: 'Tất cả trạng thái',
        failed: 'Thất bại',
        allCategories: 'Tất cả loại',
        cat_unknown: 'Không xác định',
        cat_passwordReset: 'Đặt lại mật khẩu',
        cat_register: 'Đăng ký',
        cat_userInvite: 'Mời người dùng',
        cat_emailSenderVerify: 'Xác minh người gửi',
        cat_profileEmailChange: 'Đổi email hồ sơ',
        cat_phoneNumberAlert: 'Cảnh báo số điện thoại',
        cat_faultDetection: 'Phát hiện vi phạm',
        cat_kyc: 'KYC',
        cat_invoiceChange: 'Thay đổi hoá đơn',
        cat_automation: 'Tự động hoá',
        cat_survey: 'Khảo sát',
        cat_other: 'Khác',
      }
    }
  }
}
</script>

<style scoped>
.log-list { display: flex; flex-direction: column; gap: 8px; }
.log-card {
  display: flex; align-items: flex-start; background: #fff;
  border: 1px solid #e8eaed; border-left: 4px solid #888; border-radius: 6px;
  padding: 12px 16px; cursor: pointer; transition: all 0.15s; gap: 16px;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0,0,0,0.06); transform: translateY(-1px); }
.log-card--error { background: #fef6f6; }

.log-card__time { min-width: 110px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }

.log-card__type { min-width: 150px; flex-shrink: 0; }
.log-type-pill {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  color: #fff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; white-space: nowrap;
}

.log-card__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.log-row-1 { font-size: 14px; color: #222; }
.log-row-2 { font-size: 13px; color: #555; word-break: break-all; }
.log-row-3 { font-size: 12px; }

.log-card__status { display: flex; align-items: flex-start; padding-top: 2px; flex-shrink: 0; }
.log-card__chev { display: flex; align-items: center; color: #bbb; flex-shrink: 0; }

.log-meta-grid {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 12px; padding: 12px; background: #f8f9fa; border-radius: 6px;
}
.log-meta-cell--wide { grid-column: span 2; }
.log-meta-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }

.log-section { margin-top: 20px; }
.log-section__label--big {
  font-size: 14px; color: #222; font-weight: 600;
  padding-bottom: 6px; border-bottom: 1px solid #e8eaed; margin-bottom: 8px;
}
.log-content-block {
  background: #f6f8fa; padding: 12px; border-radius: 4px;
  word-break: break-word; font-size: 14px; line-height: 1.6;
}
.log-html-content { background: #fff; border: 1px solid #e8eaed; }
.log-html-content >>> img { max-width: 100%; }
.email-preview-frame {
  width: 100%; min-height: 300px; border: 1px solid #e8eaed;
  border-radius: 4px; background: #fff; display: block;
}
</style>
<style>.log-modal .modal-xl { max-width: 1100px; }</style>
